import LogUtil from "@/betby/utils/LogUtil";

const SESSION_TICKET_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface SessionTicketCacheEntry {
  ticket: string;
  timestamp: number;
}

interface BetbyTokenCacheEntry {
  token: string;
  mode: "gc" | "sc";
  initInfo: {
    brand_id: string;
    theme_name: string;
    lang: string;
    money_type: "gc" | "sc";
  };
  timestamp: number;
  sessionTicketKey: string; // Link to session ticket
}

// In-memory cache
let sessionTicketCache: SessionTicketCacheEntry | null = null;
let betbyTokenCache: Map<string, BetbyTokenCacheEntry> = new Map();

// Pending promise for session ticket request
let sessionTicketRequestPromise: Promise<string | null> | null = null;

// Callback to request new session ticket from host
let requestSessionTicketCallback: (() => Promise<string | null>) | null = null;

/**
 * Register callback to request new session ticket from host (Unity/native app)
 */
export function registerSessionTicketRequestCallback(
  callback: () => Promise<string | null>,
): void {
  requestSessionTicketCallback = callback;
}

export function unregisterSessionTicketRequestCallback(): void {
  requestSessionTicketCallback = null;
}

/**
 * Get cached session ticket
 * Returns null if expired or not cached
 */
export function getCachedSessionTicket(): string | null {
  if (!sessionTicketCache) {
    return null;
  }

  const elapsed = Date.now() - sessionTicketCache.timestamp;
  if (elapsed > SESSION_TICKET_TTL_MS) {
    LogUtil.info("[SessionTicketCache] Session ticket expired (10min TTL), clearing cache");
    sessionTicketCache = null;
    betbyTokenCache.clear();
    return null;
  }

  return sessionTicketCache.ticket;
}

/**
 * Cache session ticket with current timestamp
 */
export function cacheSessionTicket(ticket: string): void {
  sessionTicketCache = {
    ticket,
    timestamp: Date.now(),
  };
  LogUtil.info("[SessionTicketCache] Session ticket cached, TTL: 10min");
}

/**
 * Clear session ticket cache
 */
export function clearSessionTicketCache(): void {
  sessionTicketCache = null;
  betbyTokenCache.clear();
  LogUtil.info("[SessionTicketCache] Cache cleared");
}

/**
 * Check if session ticket is expired or missing
 */
export function isSessionTicketExpired(): boolean {
  if (!sessionTicketCache) {
    return true;
  }
  return Date.now() - sessionTicketCache.timestamp > SESSION_TICKET_TTL_MS;
}

/**
 * Request new session ticket from host
 * Only one request at a time (deduplicated)
 */
export async function requestNewSessionTicket(): Promise<string | null> {
  // If already requesting, return existing promise
  if (sessionTicketRequestPromise) {
    return sessionTicketRequestPromise;
  }

  if (!requestSessionTicketCallback) {
    LogUtil.warn(
      "[SessionTicketCache] No callback registered to request session ticket",
    );
    return null;
  }

  sessionTicketRequestPromise = requestSessionTicketCallback()
    .then((ticket) => {
      if (ticket) {
        cacheSessionTicket(ticket);
      }
      return ticket;
    })
    .finally(() => {
      sessionTicketRequestPromise = null;
    });

  return sessionTicketRequestPromise;
}

/**
 * Get cached betby token for a given mode
 * Returns null if expired, session ticket changed, or not cached
 */
export function getCachedBetbyToken(mode: "gc" | "sc"): string | null {
  const entry = betbyTokenCache.get(mode);
  if (!entry) {
    return null;
  }

  // Check if linked session ticket is still valid
  if (!sessionTicketCache || entry.sessionTicketKey !== sessionTicketCache.ticket) {
    LogUtil.info(
      "[BetbyTokenCache] Token cache invalid - session ticket changed",
    );
    betbyTokenCache.delete(mode);
    return null;
  }

  // Check TTL (same as session ticket TTL)
  const elapsed = Date.now() - entry.timestamp;
  if (elapsed > SESSION_TICKET_TTL_MS) {
    LogUtil.info("[BetbyTokenCache] Token expired for mode:", mode);
    betbyTokenCache.delete(mode);
    return null;
  }

  return entry.token;
}

/**
 * Cache betby token with init info
 */
export function cacheBetbyToken(
  token: string,
  mode: "gc" | "sc",
  initInfo: {
    brand_id: string;
    theme_name: string;
    lang: string;
    money_type: "gc" | "sc";
    bet_by_token: string;
  },
): void {
  if (!sessionTicketCache) {
    LogUtil.warn(
      "[BetbyTokenCache] Cannot cache token without session ticket",
    );
    return;
  }

  betbyTokenCache.set(mode, {
    token,
    mode,
    initInfo: {
      brand_id: initInfo.brand_id,
      theme_name: initInfo.theme_name,
      lang: initInfo.lang,
      money_type: initInfo.money_type,
    },
    timestamp: Date.now(),
    sessionTicketKey: sessionTicketCache.ticket,
  });

  LogUtil.info("[BetbyTokenCache] Token cached for mode:", mode);
}

/**
 * Get cached init info for a given mode
 */
export function getCachedInitInfo(
  mode: "gc" | "sc",
): BetbyTokenCacheEntry["initInfo"] | null {
  const entry = betbyTokenCache.get(mode);
  if (!entry) {
    return null;
  }

  // Validate session ticket link
  if (!sessionTicketCache || entry.sessionTicketKey !== sessionTicketCache.ticket) {
    return null;
  }

  // Check TTL
  if (Date.now() - entry.timestamp > SESSION_TICKET_TTL_MS) {
    return null;
  }

  return entry.initInfo;
}

/**
 * Clear betby token cache for a specific mode or all
 */
export function clearBetbyTokenCache(mode?: "gc" | "sc"): void {
  if (mode) {
    betbyTokenCache.delete(mode);
  } else {
    betbyTokenCache.clear();
  }
}

/**
 * Reset all caches
 */
export function resetAllCaches(): void {
  sessionTicketCache = null;
  betbyTokenCache.clear();
  sessionTicketRequestPromise = null;
  LogUtil.info("[SessionTicketCache] All caches reset");
}